import React, { useRef, useState, useEffect } from 'react';
import './App.css';

function App() {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentColor, setCurrentColor] = useState('#000000');
  const [currentBrushSize, setCurrentBrushSize] = useState(5);
  const [currentTool, setCurrentTool] = useState('brush');
  const [shapes, setShapes] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedShape, setDraggedShape] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [snapshot, setSnapshot] = useState(null);

  const ctxRef = useRef(null);

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctxRef.current = ctx;

    const resizeCanvas = () => {
      const container = canvas.parentElement;
      const maxWidth = container.clientWidth - 40;
      const maxHeight = window.innerHeight - 300;
      
      canvas.width = Math.min(800, maxWidth);
      canvas.height = Math.min(600, maxHeight);
      
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      redrawShapes(shapes);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'z') {
        e.preventDefault();
        clearCanvas();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const hexToRgba = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b, 255];
  };

  const colorsMatch = (a, b) => {
    return a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
  };

  const getPixelColor = (imageData, x, y) => {
    const index = (Math.floor(y) * imageData.width + Math.floor(x)) * 4;
    return [
      imageData.data[index],
      imageData.data[index + 1],
      imageData.data[index + 2],
      imageData.data[index + 3]
    ];
  };

  const setPixelColor = (imageData, x, y, color) => {
    const index = (Math.floor(y) * imageData.width + Math.floor(x)) * 4;
    imageData.data[index] = color[0];
    imageData.data[index + 1] = color[1];
    imageData.data[index + 2] = color[2];
    imageData.data[index + 3] = color[3];
  };

  const floodFill = (x, y, fillColor) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const targetColor = getPixelColor(imageData, x, y);
    
    if (colorsMatch(targetColor, fillColor)) return;
    
    const pixelsToCheck = [[x, y]];
    const filledPixels = new Set();
    
    while (pixelsToCheck.length > 0) {
      const [currentX, currentY] = pixelsToCheck.pop();
      const key = `${currentX},${currentY}`;
      
      if (filledPixels.has(key)) continue;
      if (currentX < 0 || currentX >= canvas.width || currentY < 0 || currentY >= canvas.height) continue;
      
      const currentColor = getPixelColor(imageData, currentX, currentY);
      if (!colorsMatch(currentColor, targetColor)) continue;
      
      setPixelColor(imageData, currentX, currentY, fillColor);
      filledPixels.add(key);
      
      pixelsToCheck.push([currentX + 1, currentY]);
      pixelsToCheck.push([currentX - 1, currentY]);
      pixelsToCheck.push([currentX, currentY + 1]);
      pixelsToCheck.push([currentX, currentY - 1]);
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  const getShapeAtPosition = (x, y) => {
    for (let i = shapes.length - 1; i >= 0; i--) {
      const shape = shapes[i];
      const tolerance = 20;
      
      if (shape.type === 'text') {
        if (x >= shape.x - tolerance && x <= shape.x + 100 &&
            y >= shape.y - shape.size && y <= shape.y + tolerance) {
          return { shape, index: i };
        }
      } else if (x >= shape.x - tolerance && x <= shape.x + shape.width + tolerance &&
                 y >= shape.y - tolerance && y <= shape.y + shape.height + tolerance) {
        return { shape, index: i };
      }
    }
    return null;
  };

  const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius, color, size) => {
    let rot = Math.PI / 2 * 3;
    const step = Math.PI / spikes;
    
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    
    for (let i = 0; i < spikes; i++) {
      let x = cx + Math.cos(rot) * outerRadius;
      let y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;
      
      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.stroke();
  };

  const drawArrow = (ctx, fromX, fromY, toX, toY, color, size) => {
    const headLength = size * 3;
    const angle = Math.atan2(toY - fromY, toX - fromX);
    
    ctx.strokeStyle = color;
    ctx.lineWidth = size;
    ctx.beginPath();
    ctx.moveTo(fromX, fromY);
    ctx.lineTo(toX, toY);
    ctx.stroke();
    
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle - Math.PI / 6), toY - headLength * Math.sin(angle - Math.PI / 6));
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - headLength * Math.cos(angle + Math.PI / 6), toY - headLength * Math.sin(angle + Math.PI / 6));
    ctx.stroke();
  };

  const renderShape = (shape) => {
    const ctx = ctxRef.current;
    ctx.strokeStyle = shape.color;
    ctx.lineWidth = shape.size;
    ctx.fillStyle = shape.color;
    
    if (shape.type === 'text') {
      ctx.font = `${shape.size}px Arial`;
      ctx.fillText(shape.text, shape.x, shape.y);
    } else if (shape.type === 'line') {
      ctx.beginPath();
      ctx.moveTo(shape.x, shape.y);
      ctx.lineTo(shape.x + shape.width, shape.y + shape.height);
      ctx.stroke();
    } else if (shape.type === 'rectangle') {
      ctx.strokeRect(shape.x, shape.y, shape.width, shape.height);
    } else if (shape.type === 'circle') {
      ctx.beginPath();
      const radius = Math.sqrt(shape.width * shape.width + shape.height * shape.height);
      ctx.arc(shape.x, shape.y, radius, 0, Math.PI * 2);
      ctx.stroke();
    } else if (shape.type === 'triangle') {
      ctx.beginPath();
      ctx.moveTo(shape.x, shape.y);
      ctx.lineTo(shape.x + shape.width, shape.y + shape.height);
      ctx.lineTo(shape.x - shape.width, shape.y + shape.height);
      ctx.closePath();
      ctx.stroke();
    } else if (shape.type === 'star') {
      drawStar(ctx, shape.x, shape.y, 5, Math.abs(shape.width) / 2, Math.abs(shape.width) / 4, shape.color, shape.size);
    } else if (shape.type === 'arrow') {
      drawArrow(ctx, shape.x, shape.y, shape.x + shape.width, shape.y + shape.height, shape.color, shape.size);
    }
  };

  const redrawShapes = (shapesToDraw) => {
    shapesToDraw.forEach(shape => {
      renderShape(shape);
    });
  };

  const redrawCanvas = (shapesToDraw) => {
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    redrawShapes(shapesToDraw);
  };

  const startDrawing = (e) => {
    const pos = getPosition(e);
    const ctx = ctxRef.current;
    const canvas = canvasRef.current;

    // Check for shape selection
    const clickedShape = getShapeAtPosition(pos.x, pos.y);
    if (clickedShape && currentTool !== 'brush' && currentTool !== 'bucket' && currentTool !== 'text') {
      setIsDragging(true);
      setDraggedShape(clickedShape);
      setDragOffset({
        x: pos.x - clickedShape.shape.x,
        y: pos.y - clickedShape.shape.y
      });
      return;
    }

    // Paint bucket
    if (currentTool === 'bucket') {
      floodFill(Math.floor(pos.x), Math.floor(pos.y), hexToRgba(currentColor));
      return;
    }

    // Text tool
    if (currentTool === 'text') {
      const text = prompt('Enter text:');
      if (text) {
        const newShape = {
          type: 'text',
          x: pos.x,
          y: pos.y,
          text: text,
          color: currentColor,
          size: currentBrushSize * 4
        };
        setShapes(prev => [...prev, newShape]);
        setTimeout(() => redrawCanvas([...shapes, newShape]), 0);
      }
      return;
    }

    setIsDrawing(true);
    setStartPos({ x: pos.x, y: pos.y });

    if (currentTool !== 'brush') {
      setSnapshot(ctx.getImageData(0, 0, canvas.width, canvas.height));
    }

    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const draw = (e) => {
    e.preventDefault();
    const pos = getPosition(e);
    const ctx = ctxRef.current;

    // Handle dragging
    if (isDragging && draggedShape) {
      const updatedShapes = [...shapes];
      updatedShapes[draggedShape.index] = {
        ...draggedShape.shape,
        x: pos.x - dragOffset.x,
        y: pos.y - dragOffset.y
      };
      setShapes(updatedShapes);
      redrawCanvas(updatedShapes);
      return;
    }

    if (!isDrawing) return;

    if (currentTool === 'brush') {
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentBrushSize;
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else {
      ctx.putImageData(snapshot, 0, 0);
      redrawShapes(shapes);

      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentBrushSize;

      const width = pos.x - startPos.x;
      const height = pos.y - startPos.y;

      if (currentTool === 'line') {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else if (currentTool === 'rectangle') {
        ctx.strokeRect(startPos.x, startPos.y, width, height);
      } else if (currentTool === 'circle') {
        ctx.beginPath();
        const radius = Math.sqrt(width * width + height * height);
        ctx.arc(startPos.x, startPos.y, radius, 0, Math.PI * 2);
        ctx.stroke();
      } else if (currentTool === 'triangle') {
        ctx.beginPath();
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.lineTo(startPos.x - (pos.x - startPos.x), pos.y);
        ctx.closePath();
        ctx.stroke();
      } else if (currentTool === 'star') {
        drawStar(ctx, startPos.x, startPos.y, 5, Math.abs(width) / 2, Math.abs(width) / 4, currentColor, currentBrushSize);
      } else if (currentTool === 'arrow') {
        drawArrow(ctx, startPos.x, startPos.y, pos.x, pos.y, currentColor, currentBrushSize);
      }
    }
  };

  const stopDrawing = (e) => {
    if (isDragging) {
      setIsDragging(false);
      setDraggedShape(null);
      return;
    }

    if (isDrawing && currentTool !== 'brush') {
      const pos = getPosition(e);
      const width = pos.x - startPos.x;
      const height = pos.y - startPos.y;

      const newShape = {
        type: currentTool,
        x: startPos.x,
        y: startPos.y,
        width: width,
        height: height,
        color: currentColor,
        size: currentBrushSize
      };
      setShapes(prev => [...prev, newShape]);
    }

    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (window.confirm('Are you sure you want to clear the canvas?')) {
      const ctx = ctxRef.current;
      const canvas = canvasRef.current;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setShapes([]);
    }
  };

  const saveImage = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    link.download = `drawing-${timestamp}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const setActiveTool = (tool) => {
    setCurrentTool(tool);
  };

  const getCursor = () => {
    if (currentTool === 'bucket') return 'pointer';
    if (currentTool === 'text') return 'text';
    return 'crosshair';
  };

  return (
    <div className="App">
      <div className="container">
        <header>
          <h1>DRAWING APP</h1>
        </header>
        
        <div className="toolbar">
          <div className="tool-group">
            <label>Tools:</label>
            <button 
              className={`btn btn-tool ${currentTool === 'brush' ? 'active' : ''}`}
              onClick={() => setActiveTool('brush')}
            >
              ✏️ Brush
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'bucket' ? 'active' : ''}`}
              onClick={() => setActiveTool('bucket')}
            >
              🪣 Fill
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'text' ? 'active' : ''}`}
              onClick={() => setActiveTool('text')}
            >
              📝 Text
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'line' ? 'active' : ''}`}
              onClick={() => setActiveTool('line')}
            >
              📏 Line
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'rectangle' ? 'active' : ''}`}
              onClick={() => setActiveTool('rectangle')}
            >
              ⬜ Rectangle
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'circle' ? 'active' : ''}`}
              onClick={() => setActiveTool('circle')}
            >
              ⭕ Circle
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'triangle' ? 'active' : ''}`}
              onClick={() => setActiveTool('triangle')}
            >
              🔺 Triangle
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'star' ? 'active' : ''}`}
              onClick={() => setActiveTool('star')}
            >
              ⭐ Star
            </button>
            <button 
              className={`btn btn-tool ${currentTool === 'arrow' ? 'active' : ''}`}
              onClick={() => setActiveTool('arrow')}
            >
              ➡️ Arrow
            </button>
          </div>
          
          <div className="tool-group">
            <label htmlFor="colorPicker">Color:</label>
            <input 
              type="color" 
              id="colorPicker" 
              value={currentColor}
              onChange={(e) => setCurrentColor(e.target.value)}
            />
          </div>
          
          <div className="tool-group">
            <label htmlFor="brushSize">Size: <span>{currentBrushSize}</span>px</label>
            <input 
              type="range" 
              id="brushSize" 
              min="1" 
              max="50" 
              value={currentBrushSize}
              onChange={(e) => setCurrentBrushSize(parseInt(e.target.value))}
            />
          </div>
          
          <div className="tool-group">
            <button className="btn btn-clear" onClick={clearCanvas}>
              🗑️ Clear
            </button>
            <button className="btn btn-save" onClick={saveImage}>
              💾 Save
            </button>
          </div>
        </div>
        
        <div className="canvas-container">
          <canvas 
            ref={canvasRef}
            id="drawingCanvas"
            style={{ cursor: getCursor() }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseOut={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            onTouchCancel={stopDrawing}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
